import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getStaffQuarterLocations, getStaffQuarterQuarterTypes, getStaffQuarterPools } from "../../../../core/data/redux/staffQuarterSlice";

interface AddFlatModalProps {
    show: boolean;
    onHide: () => void;
    onAdd: (flatData: any) => any;
}

const AddFlatModal: React.FC<AddFlatModalProps> = ({ show, onHide, onAdd }) => {
    const dispatch = useDispatch<any>();
    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);

    const [formData, setFormData] = useState({
        flat_id: "",
        location: "",
        block: "",
        flat_no: "",
        quarter_type_code: null,
        pool_type_id: null,
        notes: ""
    });

    const [locations, setLocations] = useState<any[]>([]);
    const [quarterTypes, setQuarterTypes] = useState<any[]>([]);
    const [pools, setPools] = useState<any[]>([]);

    useEffect(() => {
        if (show) {
            // Fetch locations if not already in state
            dispatch(getStaffQuarterLocations({})).then((res: any) => {
                if (res.payload?.success) {
                    setLocations(res.payload.data || []);
                }
            });
            // Fetch quarter types if not already in state
            dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
                if (res.payload?.success) {
                    setQuarterTypes(res.payload.data || []);
                }
            });
            // Fetch pools
            dispatch(getStaffQuarterPools({})).then((res: any) => {
                if (res.payload?.success) {
                    setPools(res.payload.data || []);
                }
            });
        }
    }, [show, dispatch]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Basic validation
        if (!formData.flat_id || !formData.location || !formData.block || !formData.flat_no || !formData.quarter_type_code) {
            toast.error("Please fill in all required fields");
            return;
        }

        try {
            const payloadToSend = {
                ...formData,
                quarter_type_code: formData.quarter_type_code ? Number(formData.quarter_type_code) : null,
                pool_type_id: formData.pool_type_id ? Number(formData.pool_type_id) : null,
                location: formData.location // Send the location code/id as selected
            };

            const res = await onAdd(payloadToSend);

            if (res?.payload?.success) {
                toast.success(res.payload.message || "Flat added successfully");
                setFormData({
                    flat_id: "", location: "", block: "", flat_no: "", quarter_type_code: null, pool_type_id: null, notes: ""
                });
                onHide();
            } else {
                toast.error(res?.payload?.message || res?.payload || "Failed to add flat");
            }
        } catch (error) {
            toast.error("An unexpected error occurred");
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                    <div className="modal-header bg-light" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="ti ti-plus me-2 text-primary"></i>Add New Flat
                        </h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>
                    <div className="modal-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                {/* Location */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Location <span className="text-danger">*</span></label>
                                    <select className="form-select" name="location" value={formData.location} onChange={handleChange}>
                                        <option value="">Select Location</option>
                                        {locations.map((loc: any) => (
                                            <option key={loc.id} value={loc.code || loc.id}>{loc.name} {loc.code ? `(${loc.code})` : ''}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Block */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Block <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="block" placeholder="e.g. A1, B2" value={formData.block} onChange={handleChange} />
                                </div>

                                {/* Flat ID */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Flat ID <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="flat_id" placeholder="e.g. DMRC-T4-999" value={formData.flat_id} onChange={handleChange} />
                                </div>

                                {/* Flat No */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Flat No <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="flat_no" placeholder="e.g. 101, 204" value={formData.flat_no} onChange={handleChange} />
                                </div>

                                {/* Type */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Quarter Type <span className="text-danger">*</span></label>
                                    <select className="form-select" name="quarter_type_code" value={formData.quarter_type_code || ""} onChange={handleChange}>
                                        <option value="">Select Type</option>
                                        {quarterTypes.map((type: any) => (
                                            <option key={type.code} value={type.code}>{type.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Pool */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Pool</label>
                                    <select className="form-select" name="pool_type_id" value={formData.pool_type_id || ""} onChange={handleChange}>
                                        <option value="">Select Pool</option>
                                        {pools.map((p: any) => (
                                            <option key={p.id} value={p.id}>{p.namePool}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer bg-white border-top p-4" style={{ borderRadius: '0 0 15px 15px' }}>
                        <div className="d-flex justify-content-end gap-3 w-100">
                            <button type="button" className="btn btn-outline-secondary px-4 fw-bold" style={{ borderRadius: '8px' }} onClick={onHide}>Cancel</button>
                            <button type="button" className="btn btn-danger px-4 fw-bold d-flex align-items-center" style={{ borderRadius: '8px' }} onClick={handleSubmit}>
                                Save Flat
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddFlatModal;
